import React, { forwardRef, ComponentPropsWithRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";
import { SliderButton } from "../SliderButton";
import { EqualHeight } from "../EqualHeight/EqualHeight";
import { TileImageTeaserTile } from "./TileImageTeaserTile";
import {
  TileImageTeaserProvider,
  TileImageTeaserMode,
} from "./TileImageTeaserContext";
import { useUniqueId } from "../hooks/useUniqueId";

import styles from "./TileImageTeaser.scss";

type TileImageTeaserBaseProps = {
  mode?: TileImageTeaserMode;
  customClass?: string;
};

export type TileImageTeaserProps = TileImageTeaserBaseProps &
  Omit<React.ComponentPropsWithoutRef<"div">, keyof TileImageTeaserBaseProps>;

const calcMaxItemsPerView = (childCount: number) => {
  if (childCount <= 2) {
    return 2;
  }
  if (childCount <= 3) {
    return 3;
  }

  return 4;
};

const defaultProps = {
  mode: "image-top",
  children: undefined,
  customClass: undefined,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  TileImageTeaserBaseProps,
  typeof defaultProps
> = defaultProps;

export const TileImageTeaserWithRef = (
  props: TileImageTeaserProps,
  forwardedRef: ComponentPropsWithRef<"div">["ref"]
): JSX.Element => {
  const uid = useUniqueId<TileImageTeaserProps>("TileImageTeaser", props);
  const { children, mode = "image-top", customClass, ...restProps } = props;

  return (
    <div
      className={cx(
        styles.wrapper,
        customClass,
        styles[
          `max-items-per-view-${calcMaxItemsPerView(
            React.Children.count(children)
          )}` as keyof typeof styles
        ]
      )}
      ref={forwardedRef}
      {...restProps}
    >
      <SliderButton
        variant="backwards"
        customClass={styles["slider-button-left"]}
        id={`slider-button-${uid}-prev`}
      />
      <TileImageTeaserProvider mode={mode}>
        <EqualHeight responsiveUpdate>
          <Swiper
            watchOverflow
            navigation={{
              nextEl: `#slider-button-${uid}-next`,
              prevEl: `#slider-button-${uid}-prev`,
              disabledClass: ".slider-button-disabled",
            }}
            slidesPerView="auto"
          >
            {React.Children.toArray(children).map((child, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <SwiperSlide key={index}>{child}</SwiperSlide>
            ))}
          </Swiper>
        </EqualHeight>
      </TileImageTeaserProvider>
      <SliderButton
        variant="forwards"
        customClass={styles["slider-button-right"]}
        id={`slider-button-${uid}-next`}
      />
    </div>
  );
};

export const TileImageTeaser = (forwardRef(
  TileImageTeaserWithRef
) as unknown) as ((
  props: TileImageTeaserProps
) => ReturnType<typeof TileImageTeaserWithRef>) &
  ReactComponentStaticProps<TileImageTeaserProps> & {
    Tile: typeof TileImageTeaserTile;
  };

TileImageTeaser.Tile = TileImageTeaserTile;

TileImageTeaser.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  mode: PropTypes.oneOf(["image-top", "image-right"]),
};

TileImageTeaser.defaultProps = validatedDefaultProps;
TileImageTeaser.displayName = "TileImageTeaser";

export default TileImageTeaser;
